/**
 * API · core.notifications (fuente adicional de la campanita).
 *
 * El dueño solo LEE y MARCA leído (RLS own-row). Las filas las crean el trigger
 * de asignaciones y el cron de pendientes viejas (contexto definer). Por eso acá
 * NO hay insert/delete.
 */
import type { RealtimeChannel } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabase';

export type NotificationKind =
  | 'assignment_accepted'
  | 'assignment_declined'
  | 'assignment_stale_assignee'
  | 'assignment_stale_assigner'
  | 'chat_message'
  | 'chat_mention'
  | 'client_env_alert'
  | 'collections_payment_reported'
  | 'collections_payment_confirmed'
  | 'collections_payment_rejected'
  | 'collections_installment_overdue'
  | 'collections_installment_default'
  | 'collections_settlement_paid'
  | 'collections_contract_request'
  | 'collections_contract_loaded'
  | 'client_ticket_new'
  | 'client_ticket_message'
  | 'client_ticket_assigned'
  | 'partner_ticket_new'
  | 'partner_ticket_message'
  | 'partner_plan_change'
  | 'work_authorization_signed'
  | 'work_invoice_requested'
  | 'work_payment_informed'
  | 'social_touches_due'
  | 'campaign_email_bounced'
  | 'campaign_sender_down'
  | 'campaign_opt_out'
  | 'campaign_opt_out_doubt'
  | 'task_mention'
  | 'prospect_merged'
  | 'prospect_access_requested'
  | 'prospect_access_decided'
  | 'privacidad_retencion'
  | 'lead_web_received';

export interface NotificationPayload {
  task_id?: string;
  task_title?: string | null;
  actor_id?: string;
  actor_name?: string | null;
  assignee_id?: string;
  assignee_name?: string | null;
  reason?: string | null;
  // social_touches_due (aviso diario de toques de campaña vencidos)
  total?: number;
  toques?: Array<{
    prospect_name?: string | null;
    channel?: string | null;
    step_order?: number | null;
    campaign_id?: string | null;
  }>;
  // chat_message (DM) y chat_mention (@mención en un canal)
  channel_id?: string;
  /** Nombre del canal donde se mencionó. Un DM no lo trae. */
  channel_name?: string | null;
  message_id?: string;
  /** task_mention: el comentario donde te mencionaron (la tarea va en task_id). */
  comment_id?: string;
  sender_id?: string;
  sender_name?: string | null;
  preview?: string | null;
  // client_env_alert (Anexo H · alerta de entorno de cliente)
  environment_id?: string;
  slug?: string;
  name?: string | null;
  from?: string;
  to?: string;
  checked_at?: string;
  // collections_* (Cobranzas & Comisiones · agosto 2026)
  payment_id?: string;
  contract_id?: string;
  contract_title?: string | null;
  prospect_name?: string | null;
  amount_usd?: number;
  installment_id?: string;
  due_date?: string;
  item_title?: string | null;
  settlement_id?: string;
  note?: string | null;
  total_usd?: number;
  total_local?: number;
  currency?: string;
  salesperson_id?: string;

  // client_ticket_new / client_ticket_message (canal de soporte de clientes)
  // `message_id` ya está declarado arriba, en el bloque del chat.
  ticket_id?: string;
  ticket_title?: string | null;
  project_id?: string;
  urgency?: string;
  /** Quién escribió, del lado del cliente. Nunca una cuenta de core. */
  author_name?: string | null;
  /** client_ticket_assigned: quién asignó (cuenta de core). */
  assigned_by?: string | null;

  // partner_ticket_new / partner_ticket_message (pedidos de partners del portal).
  // También usan `ticket_id`, `ticket_title`, `prospect_id` y `urgency` de arriba.
  tenant_id?: string;
  tenant_slug?: string;
  tenant_name?: string | null;
  /** partner_ticket_new: tipo de pedido que declaró el cliente. */
  type?: string;
  /** partner_ticket_message: primeros caracteres del mensaje. */
  excerpt?: string | null;

  // partner_plan_change (cambio de plan que un cliente programó o anuló en el portal).
  /** 'programado' o 'anulado'. */
  accion?: 'programado' | 'anulado';
  plan?: string;
  plan_label?: string | null;
  monthly_hours?: number | null;
  /** Mes y año del cambio, ya en español («noviembre de 2026»). */
  period_label?: string | null;
  // También usa `actor_name` de arriba: quién lo pidió o anuló, del lado del cliente.

  // work_authorization_signed / work_invoice_requested (trabajo autorizado de un
  // partner). Usan también `tenant_name`, `project_id`, `currency`, `note` y, la
  // firma, `total` de arriba.
  work_id?: string;
  authorization_id?: string;
  /** Título del trabajo autorizado. */
  title?: string;
  signer_name?: string | null;
  /** work_invoice_requested: importe a facturar del período. */
  amount?: number;
  /** work_invoice_requested: primer día del mes (`2026-10-01`). */
  period?: string;
  /** work_payment_informed: si el cliente pidió factura. */
  quiere_factura?: boolean;

  // campaign_email_bounced (rebote que detuvo una campaña de correo) y la parte
  // de campañas detenidas del aviso diario `social_touches_due`.
  /** A quién va el aviso: al dueño de la campaña o al del prospecto. */
  rol?: 'campana' | 'prospecto';
  campaign_id?: string;
  campaign_name?: string | null;
  campaign_owner_name?: string | null;
  prospect_id?: string;
  casilla?: string;
  /** campaign_sender_down: la casilla de la campaña y por qué dejó de andar. */
  sender_email?: string | null;
  /** campaign_opt_out(_doubt): el lead respondió pidiendo la baja (o algo que se le parece). */
  conversation_id?: string;
  email?: string | null;
  texto?: string | null;
  campanas?: Array<{ id: string; name?: string | null; channel?: string | null }>;
  toques_cancelados?: number | null;
  /** privacidad_retencion: cuántos prospectos superaron los 24 meses sin interacción. */
  cantidad?: number | null;
  motivo?: string | null;
  codigo?: string | null;
  diagnostico?: string | null;
  toque?: number | null;
  bounce_id?: string;
  total_detenidas?: number;

  // lead_web_received (lead del formulario de dendev.ar)
  nombre?: string | null;
  asunto?: string | null;

  // prospect_merged (fusión de fichas duplicadas)
  survivor_id?: string;
  survivor_name?: string | null;
  merged?: Array<{ id: string; name: string | null; owner: string | null }>;
  by_name?: string | null;

  // prospect_access_requested / prospect_access_decided (pedido de acceso)
  request_id?: string;
  requester_name?: string | null;
  message?: string | null;
  approved?: boolean;
  permission?: 'view' | 'edit' | null;
  decided_by_name?: string | null;
}

export interface AppNotification {
  id: string;
  user_id: string;
  kind: NotificationKind;
  payload: NotificationPayload;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

// core.notifications todavía no está en database.types (se mantiene el archivo con
// alias hand-curated) → casting acotado, mismo patrón que taskInbox.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export async function listMyUnreadNotifications(): Promise<AppNotification[]> {
  const { data, error } = await db
    .from('notifications')
    .select('id, user_id, kind, payload, link, read_at, created_at')
    .is('read_at', null)
    .order('created_at', { ascending: false })
    .limit(20);
  if (error) throw error;
  return (data ?? []) as AppNotification[];
}

export async function markNotificationRead(id: string): Promise<void> {
  const { error } = await db
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllNotificationsRead(): Promise<void> {
  const { error } = await db
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null);
  if (error) throw error;
}

/**
 * Marca leídas las notifs de conversación de UN canal (al entrar al chat se
 * asume leído). Filtra por `payload->>channel_id`. RLS acota al propio usuario.
 *
 * Incluye `chat_mention`: entrar al canal donde te mencionaron es leer la
 * mención. Sin esto quedaría sin leer para siempre, porque nada más la marca.
 */
export async function markChannelChatNotificationsRead(channelId: string): Promise<void> {
  const { error } = await db
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null)
    .in('kind', ['chat_message', 'chat_mention'])
    .eq('payload->>channel_id', channelId);
  if (error) throw error;
}

/** Suscripción realtime a los INSERT de notificaciones del propio user. */
export function subscribeToUserNotifications(
  userId: string,
  onInsert: (n: AppNotification) => void,
): RealtimeChannel {
  const suffix = Math.random().toString(36).slice(2, 10);
  return supabase
    .channel(`notifications-${userId}-${suffix}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'core', table: 'notifications', filter: `user_id=eq.${userId}` },
      (payload) => onInsert(payload.new as unknown as AppNotification),
    )
    .subscribe();
}

/**
 * Marca leídos los avisos de UN ticket de soporte: abrirlo es haberlo visto.
 * Filtra por `payload->>ticket_id`; la RLS acota al propio usuario.
 */
export async function markTicketNotificationsRead(ticketId: string): Promise<void> {
  const { error } = await db
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .is('read_at', null)
    .in('kind', ['client_ticket_new', 'client_ticket_message', 'client_ticket_assigned', 'partner_ticket_new', 'partner_ticket_message'])
    .eq('payload->>ticket_id', ticketId);
  if (error) throw error;
}
